<template>
    <div class="tyn-root">
        <Header />
        <div class="tyn-content tyn-content-page">
            <div class="tyn-main">
                <div class="tyn-section pt-0 pb-0">

                    <div v-if="flowCompleted" class="completion-container">
                        <div class="completion-bg"></div>
                        <div class="completion-card" :class="flowCompletedVariantClass">
                            <div class="completion-icon">
                                <svg xmlns="http://www.w3.org/2000/svg"
                                     viewBox="0 0 48 48"
                                     aria-hidden="true"
                                     fill="none"
                                     stroke="currentColor"
                                     stroke-width="3"
                                     stroke-linecap="round"
                                     stroke-linejoin="round">
                                    <circle cx="24" cy="24" r="20" />
                                    <polyline points="15 24.5 21 30.5 33 18" />
                                </svg>
                                <span class="completion-spark"></span>
                            </div>
                            <div class="completion-content">
                                <h3>{{ flowCompletedTitle || 'Ação concluída com sucesso' }}</h3>
                                <p>
                                    {{ flowCompletedMessage || 'O artefato foi processado e o fluxo seguiu para a próxima etapa.' }}
                                </p>
                                <div class="completion-actions">
                                    <button class="btn-solid" @click="irParaListaProcessos">
                                        Voltar para Processos
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Erro -->
                    <div v-else-if="errorMsg" class="blocker">
                        <h3>Não foi possível abrir o artefato</h3>
                        <p class="motivo">{{ errorMsg }}</p>
                        <button class="btn" @click="tentarNovamente">Tentar novamente</button>
                    </div>

                    <div v-else class="viewer-layout">
                        <div v-if="isDocxView" class="docx-wrapper" :class="{ 'is-loading': editorBusy }">
                            <div v-if="editorBusy" class="editor-overlay">
                                <div class="overlay-content">
                                    <svg class="spinner" viewBox="0 0 50 50" aria-label="Carregando">
                                        <circle cx="25" cy="25" r="20" fill="none" stroke-width="5" />
                                    </svg>
                                    <p>Carregando documento…</p>
                                </div>
                            </div>

                            <ejs-documenteditorcontainer ref="docx"
                                                         :serviceUrl="`${urlApi}/DOCXEditor/`"
                                                         :enableToolbar="!editorBusy"
                                                         :enableTrackChanges="true"
                                                         :currentUser="nomeUsuarioEditor"
                                                         height="100%"
                                                         width="100%"
                                                         locale="pt-BR"
                                                         @created="onEditorCreated"
                                                         @beforeXmlHttpRequestSend="beforeSend"
                                                         @toolbarClick="onToolbarClick"
                                                         @trackChange="onTrackChange"
                                                         @beforeAcceptRejectChanges="onBeforeAcceptRejectChanges" />
                        </div>
                        <div v-else-if="isPdfView" class="pdf-wrapper">
                            <div ref="pdfviewer" class="pdf-host"></div>
                        </div>

                        <aside class="side-panel">
                            <div class="panel-header">
                                <div class="header-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14 2 14 8 20 8"></polyline>
                                    </svg>
                                </div>
                                <div class="header-content">
                                    <h3 class="titulo-artefato">
                                        {{ processoArtefato?.nmModeloArtefato || 'Artefato' }}
                                    </h3>
                                    <p class="sub">
                                        {{ processoArtefato?.dsNomeProcesso }}
                                    </p>
                                    <span v-if="processoArtefato?.cdProcesso" class="codigo-badge">
                                        #{{ processoArtefato.cdProcesso }}
                                    </span>
                                </div>
                            </div>

                            <div class="info-card status-card">
                                <div class="card-title">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="12 6 12 12 16 14"></polyline>
                                    </svg>
                                    Status Atual
                                </div>
                                <div class="status-badge" :class="statusCode === '5' ? 'status-aprovacao' : 'status-revisao'">
                                    {{ processoArtefato?.nmStatusAtual || '—' }}
                                </div>
                            </div>

                            <div class="info-card details-card">
                                <div class="card-title">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="16" x2="12" y2="12"></line>
                                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                    </svg>
                                    Detalhes
                                </div>
                                <div class="info-grid">
                                    <div class="info-item" v-if="processoArtefato?.dtInclusaoStatus">
                                        <span class="label">Enviado para aprovação em</span>
                                        <span class="value">{{ formatarData(processoArtefato.dtInclusaoStatus) }}</span>
                                    </div>
                                </div>
                            </div>

                            <PainelDestinatariosEtapa
                                modo="aprovacao"
                                :idProcessoArtefato="processoArtefato?.idProcessoArtefato || $route.params.id"
                                :idProcesso="processoArtefato?.idProcesso"
                                :idProcessoModeloArtefato="processoArtefato?.idProcessoModeloArtefato"
                                :cdStatusAtual="processoArtefato?.cdTipoStatusHistorico || statusCode" />

                            <div class="acoes-section">
                                <template v-if="isAprovacao">
                                    <button class="btn-action btn-danger" @click="reprovar" :disabled="loading">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="15" y1="9" x2="9" y2="15"></line>
                                            <line x1="9" y1="9" x2="15" y2="15"></line>
                                        </svg>
                                        <span>Não aprovar artefato</span>
                                    </button>
                                    <button class="btn-action btn-success" @click="aprovar" :disabled="loading">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                        </svg>
                                        <span>Aprovar artefato</span>
                                    </button>
                                </template>
                            </div>
                        </aside>
                    </div>

                </div>
            </div>
        </div>
        <Modais />
    </div>
</template>

<script>
    import Header from '@/components/Header'
    import Modais from '@/components/Modais'
    import PainelDestinatariosEtapa from '@/components/PainelDestinatariosEtapa.vue'
    import { urlApi, avisoToast, avisoErro } from '@/config/global'
    import axios from 'axios'
    import Swal from 'sweetalert2'
    import { L10n, setCulture } from '@syncfusion/ej2-base'
    import ptBR from '@syncfusion/ej2-locale/src/pt-BR.json'

    import { PdfViewer, Navigation, Magnification, Print, TextSelection, LinkAnnotation, BookmarkView, ThumbnailView } from '@syncfusion/ej2-pdfviewer'
    PdfViewer.Inject(Toolbar, Navigation, Magnification, Print, TextSelection, LinkAnnotation, BookmarkView, ThumbnailView)

    // Document Editor (Vue)
    import {
        DocumentEditorContainerComponent as EjsDocumenteditorcontainer,
        Toolbar, SpellChecker
    } from '@syncfusion/ej2-vue-documenteditor'

    import { uploadFileToS3 } from '@/services/fileUploadService'
    L10n.load(ptBR); setCulture('pt-BR')

    export default {
        name: 'ProcessoAprovacao',
        components: { Header, Modais, PainelDestinatariosEtapa, 'ejs-documenteditorcontainer': EjsDocumenteditorcontainer },
        provide: { DocumentEditorContainer: [Toolbar, SpellChecker] },
        data() {
            return {
                processoArtefato: null,
                loading: false,
                errorMsg: null,
                urlApi,
                editorBusy: false,
                consolidandoRevisoes: false,
                flowCompleted: false,
                flowCompletedTitle: '',
                flowCompletedMessage: '',
                flowCompletedVariant: 'success',
            }
        },
        computed: {
            statusCode() {
                const cd = this.processoArtefato?.cdStatusAtual
                if (cd) return String(cd)
                const fase = this.processoArtefato?.tpFaseAtual
                if (fase === 'Revisão') return '3'
                if (fase === 'Aprovação') return '5'
                return null
            },
            isAprovacao() {
                return this.statusCode === '5'
            },
            editor() {
                return this.$refs.docx?.ej2Instances?.documentEditor || null
            },
            sufixoPapelEditor() {
                return ' · Aprovador'
            },
            nomeUsuarioEditor() {
                const u = this.$store.state.user
                let nome = 'Usuário'
                if (u?.usuarioApi?.nmUsuario) nome = u.usuarioApi.nmUsuario
                else if (u?.usuario?.nmUsuario) nome = u.usuario.nmUsuario
                return `${nome}${this.sufixoPapelEditor}`
            },

            flowCompletedVariantClass() {
                return this.flowCompletedVariant === 'primary'
                    ? 'completion-card-primary'
                    : 'completion-card-success'
            },
            isEnviado() {
                const fase = this.processoArtefato?.tpFaseAtual
                return fase?.toLowerCase() === 'enviado' ||
                    fase?.toLowerCase() === 'Aguardando Revisão'.toLowerCase() ||
                    fase?.toLowerCase() === 'Revisão'.toLowerCase() ||
                    fase?.toLowerCase() === 'Aguardando Aprovação'.toLowerCase() 
            },
            isRevisado() {
                const fase = this.processoArtefato?.tpFaseAtual
                return ['revisado', 'aguardando reenvio', 'aguardando aprovação','aprovação'].includes(fase?.toLowerCase())
            },
            isDocxView() {
                const dto = this.processoArtefato || {}
                if (this.isRevisado) {
                    const extRev = this.getExt(dto.urlArquivoRevisado)
                    return extRev !== 'pdf'
                } else if (this.isEnviado) {
                    const extRev = this.getExt(dto.urlArquivoEdicao)
                    return extRev !== 'pdf'
                }
                return this.isEnviado
            },
            isPdfView() {
                const dto = this.processoArtefato || {}
                if (this.isRevisado) {
                    const extRev = this.getExt(dto?.urlArquivoRevisado)
                    if (extRev === 'pdf' && (this.isAprovado || this.isRevisado)) {
                        return true
                    }
                } else {
                    if (this.isEnviado) {
                        const extRev = this.getExt(this.processoArtefato.urlArquivo)
                        return extRev === 'pdf'
                    }

                }
                return false
            },
        },
        watch: {
            editorBusy(val) {
                const c = this.$refs.docx?.ej2Instances
                if (!c) return
                if (val && c.showSpinner) c.showSpinner()
                if (!val && c.hideSpinner) c.hideSpinner()
            }
        },
        methods: {
            setFlowCompleted(title, message, variant = 'success') {
                this.flowCompleted = true
                this.flowCompletedTitle = title || ''
                this.flowCompletedMessage = message || ''
                this.flowCompletedVariant = ['success', 'primary'].includes(variant) ? variant : 'success'
                this.processoArtefato = null
            },

            irParaListaProcessos() {
                this.$router.push({ name: 'ProcessoInteligente' })
            },

            load() {
                this.getProcessoArtefato()
            },

            async getProcessoArtefato() {
                this.loading = true
                this.errorMsg = null
                try {
                    const id = this.$route.params.id
                    const { data } = await axios.get(
                        `${urlApi}/ProcessoArtefato/GetProcessoArtefatoRevisaoAprovacao`,
                        { params: { idProcessoArtefato: id } }
                    )

                    if (data?.success) {
                        const dto = Array.isArray(data.data) ? data.data[0] : data.data
                        if (!dto) { this.setError('Artefato não encontrado.'); return }
                        this.processoArtefato = dto

                        this.$nextTick(() => {
                            if (this.isDocxView) {
                                this.abrirDocumento(dto?.urlArquivoEdicao)
                            } else {
                                if (this.isPdfView) {
                                    this.carregarPdfSomenteLeitura(dto?.urlArquivoEdicao)
                                } else {
                                    const msg = data?.msg || 'Erro ao carregar artefato.'
                                    this.setError(msg)
                                    avisoErro(msg)
                                    avisoToast('Não foi possível abrir o artefato.', 'warning')
                                }
                            }
                        })

                    } else {
                        const msg = data?.msg || 'Erro ao carregar artefato.'
                        this.setError(msg)
                        avisoErro(msg)
                        avisoToast('Não foi possível abrir o artefato.', 'warning')
                    }
                } catch (err) {
                    const payload = err.response?.data
                    const msg = (typeof payload === 'string' ? payload : payload?.msg) || err.message || 'Erro desconhecido.'
                    this.setError(msg)
                    avisoErro(msg)
                    avisoToast('Erro ao carregar artefato!', 'warning')
                } finally {
                    this.loading = false
                }
            },

            async abrirDocumento(url) {
                if (!url) { this.setError('URL do artefato vazia.'); return }

                const ext = (url.split('?')[0].split('#')[0].split('.').pop() || '').toLowerCase()
                if (ext === 'pdf') {
                    this.setError('Este artefato está em PDF. Use o visualizador de PDF para revisão.')
                    return
                }

                this.editorBusy = true
                if (this.editor) this.editor.isReadOnly = true

                try {
                    const { data: sfdt } = await axios.post(`${urlApi}/DOCXEditor/ImportFromUrl`, { url })
                    this.editor?.open(sfdt)
                    avisoToast('Documento carregado.', 'success')
                } catch (e) {
                    this.editorBusy = false
                    this.setError(e?.response?.data || e.message || 'Erro ao abrir documento no editor.')
                }
            },

            async carregarPdfSomenteLeitura(url) {
                if (!url) { this.setError('URL do artefato vazia.'); return }

                if (!this.$refs.pdfviewer) return

                const raw = this.$store?.state?.authToken || localStorage.getItem('authToken')
                const authHeader = raw?.startsWith('Bearer ') ? raw : (raw ? `Bearer ${raw}` : '')

                this.viewer = new PdfViewer({
                    serviceUrl: `${urlApi}/PdfViewer`,
                    locale: 'pt-BR',
                    ajaxRequestSettings: {
                        withCredentials: false,
                        headers: authHeader ? { Authorization: authHeader } : {}
                    },
                    // Block all modifications
                    enableAnnotation: false,
                    enableAnnotationToolbar: false,
                    enableTextMarkupAnnotation: false,
                    enableShapeAnnotation: false,
                    enableStampAnnotations: false,
                    enableFreeText: false,
                    enableHandwrittenSignature: false,
                    enableTextSearch: false,
                    enableFormFields: false,
                    enableCommentPanel: false,
                    toolbarSettings: {
                        showTooltip: true,
                        toolbarItems: ['PageNavigationTool', 'PanTool', 'ZoomInTool', 'ZoomOutTool', 'FitPageTool', 'PrintOption', 'DownloadOption']
                    },
                    height: '100%',
                    width: '100%',
                })

                this.viewer.appendTo(this.$refs.pdfviewer)

                try {
                    const { data: base64 } = await axios.get(`${urlApi}/PdfViewer/FetchAsBase64`, {
                        params: { url }
                    })
                    this.viewer.load(base64, null)
                } catch (err) {
                    this.setError('Não foi possível carregar o PDF aprovado.')
                }
            },

            garantirTrackChanges() {
                const container = this.$refs.docx?.ej2Instances
                const ed = this.editor
                const nome = this.nomeUsuarioEditor
                if (ed) {
                    ed.enableTrackChanges = true
                    if (nome) ed.currentUser = nome
                }
                if (container) {
                    container.enableTrackChanges = true
                    if (nome) container.currentUser = nome
                }
            },

            onTrackChange() {
                this.$nextTick(() => {
                    if (this.editor && !this.editor.enableTrackChanges) {
                        this.garantirTrackChanges()
                    }
                })
            },

            onBeforeAcceptRejectChanges(args) {
                if (this.consolidandoRevisoes) return
                const author = args?.author || ''
                // Bloqueia aceitar/rejeitar revisões do próprio papel; sem sufixo (legado) libera
                if (author.endsWith(this.sufixoPapelEditor)) {
                    args.cancel = true
                }
            },

            // O PDF final não pode sair com marcações, então a consolidação ignora a regra de papel
            consolidarRevisoes() {
                const revisoes = this.editor?.revisions
                if (!revisoes || revisoes.length === 0) return

                this.consolidandoRevisoes = true
                try {
                    revisoes.acceptAll()
                } finally {
                    this.consolidandoRevisoes = false
                }
            },

            onEditorCreated() {
                const ed = this.editor
                if (!ed) return

                this.garantirTrackChanges()

                ed.documentChange = () => {
                    this.garantirTrackChanges()
                    if (this.editorBusy) {
                        this.editorBusy = false
                        ed.isReadOnly = false
                        ed.focusIn()
                    }
                }
            },

            onToolbarClick(args) {
                const txt = (args?.item?.text || '').toLowerCase()
                const id = (args?.item?.id || '').toLowerCase()
                if (txt === 'novo' || txt === 'abrir' || id.includes('new') || id.includes('open')) {
                    args.cancel = true
                    return
                }
                if (this.editorBusy) {
                    args.cancel = true
                    avisoToast('Aguarde o documento terminar de carregar…', 'warning')
                }
            },

            beforeSend(args) {
                const raw = this.$store?.state?.authToken || localStorage.getItem('authToken')
                const auth = raw?.startsWith('Bearer ') ? raw : (raw ? `Bearer ${raw}` : '')
                if (auth) args.headers = [{ Authorization: auth }]
                args.withCredentials = false
            },

            setError(msg) {
                this.errorMsg = msg
                this.processoArtefato = null
            },

            formatarData(iso) {
                try { return new Date(iso).toLocaleString() } catch { return iso }
            },

            progressoStatusPayload() {
                const dto = this.processoArtefato
                if (!dto?.idProcesso || !dto?.idProcessoModeloArtefato)
                    throw new Error('IDs do processo/artefato ausentes no DTO.')
                return { idProcesso: dto.idProcesso, idProcessoModeloArtefato: dto.idProcessoModeloArtefato }
            },

            async postAcao(path, { withUrl = false, url = null, tipoAcao = null, refresh = true } = {}) {
                const body = this.progressoStatusPayload()
                const params = {}
                if (withUrl) params.urlArquivoRevisado = url
                if (tipoAcao) params.tipoAcao = tipoAcao

                const { data } = await axios.post(`${urlApi}/ProcessoArtefatoStatusHistorico/${path}`, body, { params })
                if (!data?.success) throw new Error(data?.msg || 'Operação não concluída.')
                if (refresh) await this.getProcessoArtefato()
                return data
            },

            async reprovar() {
                const result = await Swal.fire({
                    icon: 'warning',
                    title: 'Não aprovar artefato?',
                    html: `<div style="text-align:left; line-height:1.5;">
                            <p>Ao <b>não aprovar</b> o artefato:</p>
                            <ul style="margin:8px 0 0 18px; padding:0;">
                              <li>O responsável pelo artefato receberá este arquivo para <b>ajustes e reenvio</b>.</li>
                              <li>Você <b>não poderá mais alterar</b> este documento nesta etapa.</li>
                            </ul>
                            <p style="margin-top:10px;color:#6b7280;">Esta ação fará o artefato voltar para a fase de envio.</p>
                          </div>`,
                    showCancelButton: true,
                    confirmButtonText: 'Confirmar não aprovação?',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#6b7280',
                    reverseButtons: true,
                    showLoaderOnConfirm: true,
                    allowOutsideClick: () => !Swal.isLoading(),
                    preConfirm: async () => {
                        try {
                            this.loading = true
                            
                            if (this.isDocxView) {
                                await this.salvarRevisadoEChamar('ArquivoRevisado', 'docx', 'reprovado', { refresh: false })
                            } else {
                                await this.salvarRevisadoEChamarPDF('ArquivoRevisado', 'reprovado', this.processoArtefato.urlArquivoEdicao)
                            }

                            return true
                        } catch (e) {
                            Swal.showValidationMessage(e?.message || 'Erro ao não aprovar artefato.')
                            return false
                        } finally {
                            this.loading = false
                        }
                    },
                })

                if (result.isConfirmed) {
                    await Swal.fire({
                        icon: 'success',
                        title: 'Artefato não aprovado',
                        text: 'O documento foi salvo e o artefato retornou para a fase de envio.',
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#16a34a',
                    })

                    this.setFlowCompleted(
                        'Artefato não aprovado',
                        'O documento foi salvo e o artefato retornou para a fase de envio.',
                        'primary'
                    )
                }
            },

            async aprovar() {
                const result = await Swal.fire({
                    icon: 'question',
                    title: 'Confirmar aprovação do artefato?',
                    html: `<div style="text-align:left; line-height:1.5;">
                <p>Ao <b>aprovar</b> este artefato:</p>
                <ul style="margin:8px 0 0 18px; padding:0;">
                  <li>O documento será <b>salvo em formato PDF</b> e arquivado no sistema.</li>
                  <li><b>Alterações futuras</b> neste documento <b>não serão mais possíveis</b>.</li>
                  <li>O processo será encaminhado para a próxima fase, conforme o fluxo definido.</li>
                </ul>
                <p style="margin-top:10px;color:#6b7280;">Esta ação é definitiva e não poderá ser desfeita.</p>
              </div>`,
                    showCancelButton: true,
                    confirmButtonText: 'Confirmar aprovação',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#16a34a',
                    cancelButtonColor: '#6b7280',
                    reverseButtons: true,
                    focusCancel: true,
                    showLoaderOnConfirm: true,
                    allowOutsideClick: () => !Swal.isLoading(),
                    preConfirm: async () => {
                        try {
                            this.loading = true
                            
                            if (this.isDocxView) {
                                await this.salvarRevisadoEChamar('ArquivoRevisado', 'pdf', 'aprovado', { refresh: false })
                            } else {
                                await this.salvarRevisadoEChamarPDF('ArquivoRevisado', 'aprovado', this.processoArtefato.urlArquivoEdicao)
                            }

                            return true
                        } catch (e) {
                            Swal.showValidationMessage(e?.message || 'Falha ao aprovar o artefato.')
                            return false
                        } finally {
                            this.loading = false
                        }
                    },
                })

                if (result.isConfirmed) {
                    await Swal.fire({
                        icon: 'success',
                        title: 'Artefato aprovado com sucesso!',
                        text: 'O documento foi salvo em PDF e o processo seguirá para a próxima fase.',
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#16a34a',
                    })

                    this.setFlowCompleted(
                        'Artefato aprovado com sucesso!',
                        'O documento foi salvo em PDF e o processo seguirá para a próxima fase. Você pode retornar à tela de Processos para acompanhar o fluxo.',
                        'success'
                    )
                }
            },

            async exportDocxBlob() {
                const ed = this.editor
                if (!ed) throw new Error('Editor não inicializado.')
                const blob = await ed.saveAsBlob('Docx')
                return blob
            },

            getExt(url) {
                if (!url || typeof url !== 'string') return ''
                return url.split('?')[0].split('#')[0].split('.').pop().toLowerCase()
            },

            async uploadToS3(blob, contentType, destino, ext) {
                const dto = this.processoArtefato || {}
                const uploaded = await uploadFileToS3(blob, {
                    destino,
                    fileName: `artefato.${ext}`,
                    contentType,
                    idProcesso: dto.idProcesso,
                    idProcessoModeloArtefato: dto.idProcessoModeloArtefato,
                })
                return uploaded.key
            },

            async exportSfdtAsDataUrl(format = 'pdf') {
                if (!this.editor) throw new Error('Editor não inicializado.')
                if (format === 'pdf') this.consolidarRevisoes()
                const content = this.editor.serialize()
                const raw = this.$store?.state?.authToken || localStorage.getItem('authToken')
                const authHeader = raw?.startsWith('Bearer ') ? raw : (raw ? `Bearer ${raw}` : '')
                const { data: dataUrl } = await axios.post(
                    `${urlApi}/DOCXEditor/ExportBase64`,
                    { content, format, fileName: 'artefato' },
                    { headers: authHeader ? { Authorization: authHeader } : undefined }
                )
                if (!dataUrl || typeof dataUrl !== 'string') throw new Error('Falha ao exportar (retorno inválido).')
                return dataUrl
            },

            dataUrlToBlob(dataUrl) {
                const [meta, base64] = dataUrl.split(',')
                const mime = (meta.match(/data:(.*?);base64/) || [])[1] || 'application/octet-stream'
                const bytes = atob(base64)
                const buf = new Uint8Array(bytes.length)
                for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i)
                return new Blob([buf], { type: mime })
            },

            async salvarRevisadoEChamar(pathController, formato = 'pdf', tipoAcao = 'revisado', { refresh = false } = {} ) {
                let blob, mime, key
                if (formato === 'docx') {
                    blob = await this.exportDocxBlob()
                    mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    key = await this.uploadToS3(blob, mime, 'ProcessoArtefatoRevisao', 'docx')
                } else {
                    const dataUrl = await this.exportSfdtAsDataUrl(formato)
                    blob = this.dataUrlToBlob(dataUrl)
                    mime = formato === 'pdf'
                        ? 'application/pdf'
                        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    const ext = formato === 'pdf' ? 'pdf' : 'docx'
                    key = await this.uploadToS3(blob, mime, 'ProcessoArtefatoRevisao', ext)
                }
                await this.postAcao(pathController, { withUrl: true, url: key, tipoAcao, refresh })
                return key
            },

            tentarNovamente() {
                this.getProcessoArtefato()
            },

            async salvarRevisadoEChamarPDF(pathController, tipoAcao = 'revisado', urlArquivo, { refresh = false } = {}) {
                await this.postAcao(pathController, { withUrl: true, url: urlArquivo, tipoAcao, refresh })
                return urlArquivo
            },
        },

        mounted() {
            this.load()
        },
        beforeDestroy() {
        }
    }
</script>

<style scoped>
    /* Esconde os dois itens (match por aria-label e title em PT-BR) */
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[aria-label^="Crie um novo documento"]),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[title^="Crie um novo documento"]),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[aria-label^="Abra um documento"]),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[title^="Abra um documento"]) {
        display: none !important;
    }

    /* Versão mais genérica (case-insensitive) — use se mudar o texto */
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[aria-label*="novo documento" i]),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[title*="novo documento" i]),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[aria-label*="abra um documento" i]),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[title*="abra um documento" i]) {
        display: none !important;
    }

    /* Some com o separador imediatamente antes desses itens (suporta e-separator e e-tbar-separator) */
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-separator:has(+ .e-toolbar-item[aria-label^="Crie um novo documento"])),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-separator:has(+ .e-toolbar-item[aria-label^="Abra um documento"])),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-tbar-separator:has(+ .e-toolbar-item[aria-label^="Crie um novo documento"])),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-tbar-separator:has(+ .e-toolbar-item[aria-label^="Abra um documento"])) {
        display: none !important;
    }

    .blocker-success {
        border-color: #16a34a;
        color: #065f46;
    }

    .completion-container {
        position: relative;
        height: calc(100vh - var(--appbar-height));
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 32px;
        overflow: hidden;
    }

    .completion-bg {
        position: absolute;
        inset: 0;
        background: radial-gradient(circle at 20% 20%, rgba(56, 189, 248, 0.25), transparent 55%),
                    radial-gradient(circle at 80% 30%, rgba(16, 185, 129, 0.35), transparent 60%),
                    radial-gradient(circle at 50% 80%, rgba(59, 130, 246, 0.3), transparent 55%);
        filter: blur(60px);
        opacity: 0.65;
        transform: scale(1.2);
        pointer-events: none;
    }

    .completion-card {
        position: relative;
        display: flex;
        align-items: center;
        gap: 36px;
        padding: 36px 40px;
        border-radius: 28px;
        box-shadow: 0 30px 70px rgba(15, 23, 42, 0.12);
        backdrop-filter: blur(12px);
        color: #0f172a;
        overflow: hidden;
        max-width: 820px;
        width: 100%;
        animation: fadeUp 0.5s ease-out;
    }

    .completion-card::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        border: 1px solid rgba(255, 255, 255, 0.35);
        pointer-events: none;
    }

    .completion-card-success {
        background: linear-gradient(135deg, rgba(110, 231, 183, 0.25), rgba(5, 150, 105, 0.4));
    }

    .completion-card-primary {
        background: linear-gradient(135deg, rgba(96, 165, 250, 0.25), rgba(37, 99, 235, 0.45));
    }

    .completion-icon {
        position: relative;
        flex-shrink: 0;
        width: 120px;
        height: 120px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        background: rgba(255, 255, 255, 0.45);
        color: #0f172a;
        box-shadow: 0 20px 45px rgba(15, 23, 42, 0.15);
    }

    .completion-icon svg {
        width: 60px;
        height: 60px;
    }

    .completion-spark {
        position: absolute;
        inset: 8px;
        border-radius: inherit;
        border: 1px solid rgba(15, 23, 42, 0.08);
        animation: pulse 2.8s ease-in-out infinite;
    }

    .completion-content {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    .completion-content h3 {
        font-size: 2rem;
        font-weight: 700;
        color: #0f172a;
        margin: 0;
        line-height: 1.2;
    }

    .completion-content p {
        font-size: 1rem;
        line-height: 1.6;
        color: rgba(15, 23, 42, 0.75);
        margin: 0;
        max-width: 520px;
    }

    .completion-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
    }

    .completion-actions button {
        min-width: 190px;
    }

    .btn-solid,
    .btn-outline {
        border-radius: 999px;
        padding: 12px 24px;
        font-weight: 600;
        font-size: 0.95rem;
        border: none;
        cursor: pointer;
        transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
        box-shadow: 0 14px 30px rgba(15, 23, 42, 0.15);
    }

    .btn-solid {
        background: linear-gradient(135deg, #16a34a, #0f766e);
        color: #fff;
    }

    .btn-solid:hover {
        transform: translateY(-2px);
        box-shadow: 0 18px 40px rgba(15, 23, 42, 0.2);
        background: linear-gradient(135deg, #15803d, #0d9488);
    }

    .btn-outline {
        background: rgba(255, 255, 255, 0.9);
        color: #0f172a;
        border: 1px solid rgba(15, 23, 42, 0.1);
        backdrop-filter: blur(6px);
    }

    .btn-outline:hover {
        transform: translateY(-2px);
        box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
        background: rgba(255, 255, 255, 0.98);
    }

    .completion-card-primary .btn-solid {
        background: linear-gradient(135deg, #2563eb, #1d4ed8);
    }

    .completion-card-primary .btn-solid:hover {
        background: linear-gradient(135deg, #1d4ed8, #1e40af);
    }

    @keyframes fadeUp {
        from {
            opacity: 0;
            transform: translateY(24px);
        }

        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @keyframes pulse {
        0%, 100% {
            opacity: 0.35;
        }

        50% {
            opacity: 0.65;
        }
    }

    @media (max-width: 920px) {
        .completion-card {
            flex-direction: column;
            text-align: center;
            padding: 28px 24px 32px;
        }

        .completion-icon {
            width: 96px;
            height: 96px;
        }

        .completion-content {
            align-items: center;
        }

        .completion-content h3 {
            font-size: 1.7rem;
        }

        .completion-actions {
            justify-content: center;
        }

        .completion-actions button {
            flex: 1 1 140px;
        }
    }

    .docx-wrapper {
        flex: 3 1 0;
    }

    .pdf-wrapper {
        flex: 3 1 0;
    }

    .pdf-host {
        height: 100%;
        width: 100%;
        overflow: hidden;
    }

    .viewer-layout {
        display: flex;
        height: calc(100vh - var(--appbar-height));
    }

        .viewer-layout > * {
            min-width: 0;
            box-sizing: border-box;
        }

    .side-panel {
        flex: 1 1 0;
        max-width: 380px;
        min-width: 320px;
        padding: 24px 20px;
        display: flex;
        flex-direction: column;
        gap: 20px;
        overflow-y: auto;
        overflow-x: hidden;
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        border-left: 1px solid rgba(0, 0, 0, 0.08);
    }

    .panel-header {
        background: linear-gradient(135deg, #235FE8 0%, #00d084 100%);
        border-radius: 16px;
        padding: 20px;
        color: white;
        box-shadow: 0 8px 24px rgba(35, 95, 232, 0.25);
        display: flex;
        gap: 16px;
        align-items: flex-start;
        animation: slideInRight 0.4s ease-out;
    }

    .header-icon {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 12px;
        padding: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        backdrop-filter: blur(10px);
        flex-shrink: 0;
    }

        .header-icon svg {
            width: 24px;
            height: 24px;
            color: white;
        }

    .header-content {
        flex: 1;
        min-width: 0;
    }

    .titulo-artefato {
        margin: 0 0 8px 0;
        font-size: 1.25rem;
        font-weight: 700;
        line-height: 1.3;
        color: white;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .sub {
        margin: 0 0 10px 0;
        font-size: 0.9rem;
        opacity: 0.95;
        color: rgba(255, 255, 255, 0.95);
        line-height: 1.4;
    }

    .codigo-badge {
        display: inline-block;
        background: rgba(255, 255, 255, 0.25);
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 0.8rem;
        font-weight: 600;
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.3);
    }

    .info-card {
        background: white;
        border-radius: 14px;
        padding: 18px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        border: 1px solid rgba(0, 0, 0, 0.05);
        transition: all 0.3s ease;
        animation: slideInRight 0.4s ease-out;
        animation-fill-mode: both;
    }

        .info-card:hover {
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            transform: translateY(-2px);
        }

    .status-card {
        animation-delay: 0.1s;
    }

    .details-card {
        animation-delay: 0.2s;
    }

    .card-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.85rem;
        font-weight: 600;
        color: #6c757d;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 14px;
    }

        .card-title svg {
            opacity: 0.7;
        }

    .status-badge {
        display: inline-block;
        padding: 10px 18px;
        border-radius: 10px;
        font-weight: 600;
        font-size: 0.95rem;
        text-align: center;
        width: 100%;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
    }

    .status-revisao {
        background: linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%);
        color: #856404;
    }

    .status-aprovacao {
        background: linear-gradient(135deg, #a8e6cf 0%, #56ccf2 100%);
        color: #155724;
    }

    .info-grid {
        display: grid;
        gap: 14px;
    }

    .info-item {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 12px;
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        border-radius: 10px;
        border-left: 3px solid #235FE8;
        transition: all 0.3s ease;
    }

        .info-item:hover {
            border-left-color: #00d084;
            transform: translateX(2px);
        }

        .info-item .label {
            font-size: 0.8rem;
            font-weight: 600;
            color: #6c757d;
            text-transform: uppercase;
            letter-spacing: 0.3px;
        }

        .info-item .value {
            font-size: 0.95rem;
            font-weight: 600;
            color: #2d3748;
            line-height: 1.4;
        }

    .acoes-section {
        margin-top: auto;
        display: flex;
        flex-direction: column;
        gap: 12px;
        animation: slideInRight 0.4s ease-out;
        animation-delay: 0.3s;
        animation-fill-mode: both;
    }

    .acoes-title {
        font-size: 0.85rem;
        font-weight: 700;
        color: #495057;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 4px;
    }

    /* Botões de Ação */
    .btn-action {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 14px 20px;
        border: none;
        border-radius: 12px;
        font-size: 0.95rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        position: relative;
        overflow: hidden;
        color: white;
    }

        .btn-action::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 220%;
            height: 220%;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.3);
            transform: translate(-50%, -50%) scale(0);
            transition: transform 0.6s ease;
            pointer-events: none;
        }

        .btn-action:hover::before {
            transform: translate(-50%, -50%) scale(1);
        }

        .btn-action:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none !important;
        }

        .btn-action:not(:disabled):hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        }

        .btn-action:not(:disabled):active {
            transform: translateY(0);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .btn-action svg {
            flex-shrink: 0;
            position: relative;
            z-index: 1;
        }

        .btn-action span {
            flex: 1;
            text-align: center;
            position: relative;
            z-index: 1;
        }

    .btn-primary {
        background: linear-gradient(135deg, #235FE8 0%, #00d084 100%);
        box-shadow: 0 4px 12px rgba(35, 95, 232, 0.3);
    }

        .btn-primary:not(:disabled):hover {
            background: linear-gradient(135deg, #1e50c9 0%, #00b872 100%);
            box-shadow: 0 8px 24px rgba(35, 95, 232, 0.4);
        }

        .btn-primary:not(:disabled):active {
            background: linear-gradient(135deg, #1a47b5 0%, #009f65 100%);
        }

    .btn-success {
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }

        .btn-success:not(:disabled):hover {
            background: linear-gradient(135deg, #0ea374 0%, #047857 100%);
            box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
        }

        .btn-success:not(:disabled):active {
            background: linear-gradient(135deg, #0c8d63 0%, #036547 100%);
        }

    .btn-danger {
        background: linear-gradient(135deg, #f43f5e 0%, #e11d48 100%);
        box-shadow: 0 4px 12px rgba(244, 63, 94, 0.3);
    }

        .btn-danger:not(:disabled):hover {
            background: linear-gradient(135deg, #e11d48 0%, #be123c 100%);
            box-shadow: 0 8px 24px rgba(244, 63, 94, 0.4);
        }

        .btn-danger:not(:disabled):active {
            background: linear-gradient(135deg, #be123c 0%, #9f1239 100%);
        }

    .btn-warning {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
    }

        .btn-warning:not(:disabled):hover {
            background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
            box-shadow: 0 8px 24px rgba(245, 158, 11, 0.4);
        }

        .btn-warning:not(:disabled):active {
            background: linear-gradient(135deg, #b45309 0%, #92400e 100%);
        }

    /* Animações */
    @keyframes slideInRight {
        from {
            opacity: 0;
            transform: translateX(20px);
        }

        to {
            opacity: 1;
            transform: translateX(0);
        }
    }

    /* Scrollbar customizada */
    .side-panel::-webkit-scrollbar {
        width: 6px;
    }

    .side-panel::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.05);
        border-radius: 10px;
    }

    .side-panel::-webkit-scrollbar-thumb {
        background: linear-gradient(135deg, #235FE8 0%, #00d084 100%);
        border-radius: 10px;
    }

        .side-panel::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, #1e50c9 0%, #00b872 100%);
        }

    .blocker {
        height: calc(100vh - var(--appbar-height));
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 24px;
        gap: 12px;
    }

        .blocker .motivo {
            max-width: 720px;
            opacity: .85;
            font-size: 0.95rem;
        }

        .blocker .btn {
            padding: 10px 16px;
            border-radius: 10px;
            border: none;
            cursor: pointer;
        }

    .editor-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.95);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        backdrop-filter: blur(4px);
    }

    .overlay-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
    }

        .overlay-content p {
            font-size: 1.1rem;
            font-weight: 600;
            color: #235FE8;
            margin: 0;
        }

    .spinner {
        width: 50px;
        height: 50px;
        animation: rotate 2s linear infinite;
    }

        .spinner circle {
            stroke: #235FE8;
            stroke-linecap: round;
            animation: dash 1.5s ease-in-out infinite;
        }

    @keyframes rotate {
        100% {
            transform: rotate(360deg);
        }
    }

    @keyframes dash {
        0% {
            stroke-dasharray: 1, 150;
            stroke-dashoffset: 0;
        }

        50% {
            stroke-dasharray: 90, 150;
            stroke-dashoffset: -35;
        }

        100% {
            stroke-dasharray: 90, 150;
            stroke-dashoffset: -124;
        }
    }

    .is-loading {
        position: relative;
    }

    /* ========== DARK THEME - SIDE PANEL ONLY ========== */
    [data-bs-theme="dark"] .completion-container {
        background: linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 41, 59, 0.95) 100%);
        color: #e2e8f0;
    }

    [data-bs-theme="dark"] .completion-card {
        box-shadow: 0 30px 70px rgba(15, 23, 42, 0.45);
        color: #e2e8f0;
    }

    [data-bs-theme="dark"] .completion-card-success {
        background: linear-gradient(135deg, rgba(34, 197, 94, 0.22), rgba(5, 150, 105, 0.38));
    }

    [data-bs-theme="dark"] .completion-card-primary {
        background: linear-gradient(135deg, rgba(37, 99, 235, 0.25), rgba(59, 130, 246, 0.4));
    }

    [data-bs-theme="dark"] .completion-content p {
        color: rgba(226, 232, 240, 0.78);
    }

    [data-bs-theme="dark"] .btn-outline {
        background: rgba(226, 232, 240, 0.12);
        color: #e2e8f0;
        border-color: rgba(148, 163, 184, 0.35);
    }

    [data-bs-theme="dark"] .btn-outline:hover {
        background: rgba(226, 232, 240, 0.24);
        box-shadow: 0 18px 40px rgba(37, 99, 235, 0.25);
    }

    [data-bs-theme="dark"] .side-panel {
        background: linear-gradient(135deg, #1a1f2e 0%, #252b3d 100%);
        border-left: 1px solid rgba(255, 255, 255, 0.1);
    }

    [data-bs-theme="dark"] .panel-header {
        background: linear-gradient(135deg, #60a5fa 0%, #34d399 100%);
        box-shadow: 0 8px 24px rgba(96, 165, 250, 0.3);
    }

    [data-bs-theme="dark"] .header-icon {
        background: rgba(255, 255, 255, 0.25);
    }

    [data-bs-theme="dark"] .titulo-artefato,
    [data-bs-theme="dark"] .sub,
    [data-bs-theme="dark"] .codigo-badge {
        color: white;
    }

    [data-bs-theme="dark"] .codigo-badge {
        background: rgba(255, 255, 255, 0.2);
        border-color: rgba(255, 255, 255, 0.25);
    }

    [data-bs-theme="dark"] .info-card {
        background: linear-gradient(135deg, #252b3d 0%, #2d3548 100%);
        border-color: rgba(255, 255, 255, 0.1);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    }

    [data-bs-theme="dark"] .info-card:hover {
        box-shadow: 0 8px 24px rgba(96, 165, 250, 0.2);
        border-color: rgba(96, 165, 250, 0.3);
    }

    [data-bs-theme="dark"] .card-title {
        color: #d1d5db;
    }

    [data-bs-theme="dark"] .status-badge {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    }

    [data-bs-theme="dark"] .status-revisao {
        background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
        color: #1f2937;
        font-weight: 700;
    }

    [data-bs-theme="dark"] .status-aprovacao {
        background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
        color: #1f2937;
        font-weight: 700;
    }

    [data-bs-theme="dark"] .info-item {
        background: linear-gradient(135deg, #2d3548 0%, #353d52 100%);
        border-left-color: #60a5fa;
    }

    [data-bs-theme="dark"] .info-item:hover {
        border-left-color: #34d399;
        background: linear-gradient(135deg, #353d52 0%, #3d4558 100%);
    }

    [data-bs-theme="dark"] .info-item .label {
        color: #9ca3af;
    }

    [data-bs-theme="dark"] .info-item .value {
        color: #f3f4f6;
    }

    [data-bs-theme="dark"] .acoes-title {
        color: #d1d5db;
    }

    [data-bs-theme="dark"] .btn-action {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    }

    [data-bs-theme="dark"] .btn-action:not(:disabled):hover {
        box-shadow: 0 8px 24px rgba(96, 165, 250, 0.3);
    }

    [data-bs-theme="dark"] .btn-primary {
        background: linear-gradient(135deg, #60a5fa 0%, #34d399 100%);
        box-shadow: 0 4px 12px rgba(96, 165, 250, 0.4);
    }

    [data-bs-theme="dark"] .btn-primary:not(:disabled):hover {
        background: linear-gradient(135deg, #3b82f6 0%, #10b981 100%);
        box-shadow: 0 8px 24px rgba(96, 165, 250, 0.5);
    }

    [data-bs-theme="dark"] .btn-primary:not(:disabled):active {
        background: linear-gradient(135deg, #2563eb 0%, #059669 100%);
    }

    [data-bs-theme="dark"] .btn-success {
        background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
        box-shadow: 0 4px 12px rgba(52, 211, 153, 0.4);
    }

    [data-bs-theme="dark"] .btn-success:not(:disabled):hover {
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        box-shadow: 0 8px 24px rgba(52, 211, 153, 0.5);
    }

    [data-bs-theme="dark"] .btn-success:not(:disabled):active {
        background: linear-gradient(135deg, #059669 0%, #047857 100%);
    }

    [data-bs-theme="dark"] .btn-danger {
        background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
        box-shadow: 0 4px 12px rgba(248, 113, 113, 0.4);
    }

    [data-bs-theme="dark"] .btn-danger:not(:disabled):hover {
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        box-shadow: 0 8px 24px rgba(248, 113, 113, 0.5);
    }

    [data-bs-theme="dark"] .btn-danger:not(:disabled):active {
        background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    }

    [data-bs-theme="dark"] .btn-warning {
        background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
        box-shadow: 0 4px 12px rgba(251, 191, 36, 0.4);
    }

    [data-bs-theme="dark"] .btn-warning:not(:disabled):hover {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        box-shadow: 0 8px 24px rgba(251, 191, 36, 0.5);
    }

    [data-bs-theme="dark"] .btn-warning:not(:disabled):active {
        background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
    }

    [data-bs-theme="dark"] .side-panel::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
    }

    [data-bs-theme="dark"] .side-panel::-webkit-scrollbar-thumb {
        background: linear-gradient(135deg, #60a5fa 0%, #34d399 100%);
    }

    [data-bs-theme="dark"] .side-panel::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(135deg, #3b82f6 0%, #10b981 100%);
    }

    [data-bs-theme="dark"] .blocker {
        background: linear-gradient(135deg, #1a1f2e 0%, #252b3d 100%);
        color: #f3f4f6;
    }

    [data-bs-theme="dark"] .blocker h3 {
        color: #ffffff;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }

    [data-bs-theme="dark"] .blocker .motivo {
        color: #d1d5db;
    }

    [data-bs-theme="dark"] .blocker .btn {
        background: linear-gradient(135deg, #60a5fa 0%, #34d399 100%);
        color: white;
        box-shadow: 0 4px 12px rgba(96, 165, 250, 0.3);
        transition: all 0.3s ease;
    }

    [data-bs-theme="dark"] .blocker .btn:hover {
        background: linear-gradient(135deg, #3b82f6 0%, #10b981 100%);
        box-shadow: 0 8px 24px rgba(96, 165, 250, 0.4);
        transform: translateY(-2px);
    }

    [data-bs-theme="dark"] .editor-overlay {
        background: rgba(26, 31, 46, 0.95);
    }

    [data-bs-theme="dark"] .overlay-content p {
        color: #60a5fa;
    }

    [data-bs-theme="dark"] .spinner circle {
        stroke: #60a5fa;
    }
</style>
